/**
 * Maps Strapi case-study data into the view models used by the Customer
 * Stories page. The per-card content (person, quote, stats, logo) all comes
 * from each case study's existing Strapi fields — only the page-level copy
 * (hero headline, "Why It Works" band, FAQ) is static, in customerStoriesData.
 */
import {
  AllCaseStudyPageAttributes,
  CaseStudyTestimonial
} from '@/utils/queries/customer_case_study'

export interface Person {
  name: string
  designation: string
  photo?: string
  initials: string
}

export interface StatItem {
  value: string
  label: string
}

export interface StoryCardData {
  slug: string
  company: string
  /** Specific tag shown on the card, e.g. "Travel Marketplace". */
  category?: string
  /** Broad bucket that drives the filter tabs, e.g. "B2B SaaS". */
  segment?: string
  logo?: StrapiImageFile
  bigStat?: StatItem
  miniStats: StatItem[]
  quote?: string
  person?: Person
}

export interface FeaturedData extends StoryCardData {
  sub?: string
  pillStat?: StatItem
  statCards: StatItem[]
}

export const stripHtml = (value?: string | null): string =>
  (value ?? '').replace(/<[^>]*>/g, '').trim()

const initialsOf = (name?: string | null): string => {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  return words
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase()
}

const designationOf = (testimonial: CaseStudyTestimonial): string => {
  const role = testimonial?.authorDesignation?.trim()
  const company = testimonial?.authorCompany?.trim()
  if (role && company && !role.includes(company)) return `${role}, ${company}`
  return role || company || ''
}

export const toPerson = (
  testimonial: CaseStudyTestimonial
): Person | undefined => {
  if (!testimonial?.authorName) return undefined
  return {
    name: testimonial.authorName,
    designation: designationOf(testimonial),
    photo: testimonial.authorImage?.data?.attributes?.url,
    initials: initialsOf(testimonial.authorName)
  }
}

const toStat = (block?: StrapiBlockComponent): StatItem | undefined => {
  if (!block) return undefined
  const value = stripHtml(block.title)
  const label = stripHtml(block.description)
  if (!value && !label) return undefined
  return { value, label }
}

const logoOf = (
  attributes: AllCaseStudyPageAttributes
): StrapiImageFile | undefined => attributes.customerLogoWhite?.data?.attributes

const companyOf = (attributes: AllCaseStudyPageAttributes): string =>
  attributes.testimonial?.authorCompany?.trim() ||
  attributes.heroTitle ||
  attributes.slug

export const toStoryCard = (
  attributes: AllCaseStudyPageAttributes
): StoryCardData => {
  const stats = attributes.statisticsList ?? []
  const segment = attributes.segment?.trim().replace(/_/g, ' ') || undefined
  return {
    slug: attributes.slug,
    company: companyOf(attributes),
    segment,
    category: attributes.categoryLabel?.trim() || segment,
    logo: logoOf(attributes),
    bigStat: toStat(stats[0]),
    miniStats: [toStat(stats[1]), toStat(stats[2])].filter(
      (stat): stat is StatItem => Boolean(stat)
    ),
    quote: stripHtml(attributes.testimonial?.review) || undefined,
    person: toPerson(attributes.testimonial)
  }
}

export const toFeatured = (
  attributes: AllCaseStudyPageAttributes
): FeaturedData => {
  const stats = attributes.statisticsList ?? []
  return {
    ...toStoryCard(attributes),
    sub: attributes.heroDescription || undefined,
    pillStat: toStat(stats[1]),
    statCards: stats
      .slice(2, 4)
      .map(toStat)
      .filter((stat): stat is StatItem => Boolean(stat))
  }
}

/** People for the decorative face groups (hero collage, pills, CTA strip).
 *  De-duped by name, with photographed customers first so the prominent
 *  collage favours real headshots over initials fallbacks. */
export const collectPeople = (
  caseStudies: AllCaseStudyPageAttributes[]
): Person[] => {
  const seen = new Set<string>()
  const people = caseStudies
    .map(cs => toPerson(cs.testimonial))
    .filter((person): person is Person => Boolean(person))
    .filter(person => {
      if (seen.has(person.name)) return false
      seen.add(person.name)
      return true
    })

  // stable sort: people with a photo first
  return people
    .map((person, index) => ({ person, index }))
    .sort((a, b) => {
      const aHasPhoto = a.person.photo ? 0 : 1
      const bHasPhoto = b.person.photo ? 0 : 1
      return aHasPhoto - bHasPhoto || a.index - b.index
    })
    .map(entry => entry.person)
}
